import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

function safeRedirectPath(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export function RegisterPage() {
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirectPath(searchParams.get("redirect_to"));
  const loginHref = redirectTo === "/" ? "/login" : `/login?redirect_to=${encodeURIComponent(redirectTo)}`;
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    const result = await register(email, password, displayName);
    if (result.errors.length === 0) {
      setSuccessMessage(
        result.message || "Registracija sėkminga! Patikrinkite savo el. paštą."
      );
    } else {
      setErrors(result.errors);
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/icon.svg" alt="Boardo" className="h-12 w-12" />
            <span className="text-2xl font-bold text-primary-800">Boardo</span>
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            Registruotis
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Jau turite paskyrą?{" "}
            <Link
              to="/login"
              className="font-medium text-primary-800 hover:text-primary-700"
            >
              Prisijungti
            </Link>
          </p>
        </div>

        <div className="card p-8">
          {successMessage ? (
            <div className="text-center">
              <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-700">
                {successMessage}
              </div>
              <p className="text-sm text-gray-600">
                Negavote laiško?{" "}
                <Link
                  to={loginHref}
                  className="font-medium text-primary-800 hover:text-primary-700"
                >
                  Prisijungti
                </Link>
              </p>
            </div>
          ) : (
            <>
              {errors.length > 0 && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {errors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Vardas
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input"
                    required
                    minLength={2}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    El. paštas
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Slaptažodis
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    required
                    minLength={6}
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full">
                  {loading ? "Registruojama..." : "Registruotis"}
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">arba</span>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <form method="post" action="/users/auth/google_oauth2">
                    <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''} />
                    <button type="submit" className="btn-secondary w-full">
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Google
                    </button>
                  </form>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
